<script lang="ts" setup>
import { ElNotification } from 'element-plus'
import { computed, onMounted, onUnmounted, watchEffect } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'

import CampaignForm from '@/components/CampaignForm/CampaignForm.vue'
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import { getRecommendedPrice } from '@/lib/helpers'
import { type CampaignFormData, serialiseCampaignForm } from '@/lib/helpers/campaign.helpers'
import type { Currency } from '@/types/store'
import type { UserTag } from '@/types/userTags'

const router = useRouter()
const store = useStore()
const state = computed(() => store.getters['campaign/state'])
const userTags = computed<UserTag[]>(() => store.getters['userTags/userTags'])
const currency = computed<Currency>(() => store.getters['shop/currency'])
const locale = computed<string>(() => store.getters['shop/locale'])
const advertiserUsesCpa = computed(() => store.getters['shop/advertiserUsesCpa'])
const storeGroups = computed(() => {
  return store.getters['shop/storeGroups']?.map((item) => item.id)
})
const storeGroupId = computed<number>(() => store.getters['shop/storeGroupId'])
const trafficSources = computed(() => store.getters['trafficSources/trafficSourcesTemplates'])
const isFetched = computed(() => {
  return userTags.value && trafficSources.value
})
const recommendedPrice = computed(() => getRecommendedPrice(locale.value, currency.value))
const handleBack = async () => {
  if (store.getters['onboarding/currentOnboardingStep'] === 2) {
    await store.dispatch('onboarding/setOnboardingActivity', true)
  }
  await router.push('/campaigns')
}
const handleSubmit = async (data: CampaignFormData) => {
  const result = await store.dispatch(
    'campaign/createCampaign',
    serialiseCampaignForm(data, storeGroups.value, storeGroupId.value, currency.value.code, false),
  )
  if (result) {
    await router.push(`/campaign-users/${result.id}?created`)
  }
}

onMounted(() => store.dispatch('userTags/initUserTags'))
onMounted(() => store.dispatch('trafficSources/fetchTrafficSources'))
onUnmounted(async () => await store.dispatch('campaign/reset'))
watchEffect(() => {
  if (state.value.error) {
    ElNotification.error({
      message: 'message' in state.value.error ? state.value.error.message : "Something went wrong and your campaign wasn't created",
      duration: 0,
    })
  }
})
</script>
<template>
  <div>
    <CampaignForm
      v-if="isFetched"
      :is-loading="state.isLoading"
      :currency-symbol="currency?.symbol"
      :traffic-sources="trafficSources"
      :advertiser-uses-cpa="advertiserUsesCpa"
      :recommended-price="recommendedPrice"
      :user-tags="userTags"
      @handle-submit="handleSubmit"
      @handle-back="handleBack"
    />
    <LoadingSpinner v-else />
  </div>
</template>
